import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { MessageCircle, ArrowRight, Diamond, Award, Sparkles, Shield } from 'lucide-react';

export const GredzeniArDimantiem = () => {
  return (
    <>
      <Helmet>
        <title>Gredzeni ar dimantiem | Dimanta gredzeni Latvijā | Pure Atelier</title>
        <meta name="description" content="Gredzeni ar dimantiem Latvijā. Saderināšanās gredzeni, gredzeni ar dabīgiem dimantiem. GIA sertifikāti, labākās cenas. Pure Atelier Riga." />
        <meta name="keywords" content="gredzeni ar dimantiem, dimanta gredzeni, gredzeni ar dimantiem Latvijā, rings with diamonds Latvia" />
        <link rel="canonical" href="https://pureatelier.lv/gredzeni-ar-dimantiem" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white">
        {/* Hero Section */}
        <section className="relative py-20 lg:py-32 overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://images.pexels.com/photos/691046/pexels-photo-691046.jpeg?auto=compress&cs=tinysrgb&w=1920')] bg-cover bg-center opacity-10"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <span className="inline-block px-4 py-2 bg-amber-100 text-amber-800 rounded-full text-sm font-medium mb-6">
                Pure Atelier Riga
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-stone-900 mb-6 leading-tight">
                Gredzeni ar dimantiem
              </h1>
              <p className="text-xl text-stone-600 mb-8 max-w-2xl mx-auto">
                Izsmalcināti gredzeni ar dabīgiem, sertificētiem dimantiem. Plašs klāsts dažādos stilos un cenu kategorijās.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/gallery?category=rings">
                  <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-8">
                    Skatīt gredzenus <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <a href="https://wa.me/37128448991?text=Sveiki!%20Interesē%20gredzeni%20ar%20dimantiem." target="_blank" rel="noopener noreferrer">
                  <Button size="lg" variant="outline" className="border-amber-600 text-amber-700 hover:bg-amber-50 px-8">
                    <MessageCircle className="mr-2 h-5 w-5" /> Konsultācija
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-4 gap-8">
              {[
                { icon: Diamond, title: "Dabīgi dimanti", desc: "100% dabīgi akmeņi" },
                { icon: Award, title: "Sertificēti", desc: "GIA un IGI sertifikāti" },
                { icon: Sparkles, title: "Dažādi stili", desc: "Solitaire, Halo, Bezel" },
                { icon: Shield, title: "Garantija", desc: "Mūža garantija" }
              ].map((item, i) => (
                <div key={i} className="text-center p-6">
                  <item.icon className="h-12 w-12 text-amber-600 mx-auto mb-4" />
                  <h3 className="font-semibold text-stone-900 mb-2">{item.title}</h3>
                  <p className="text-stone-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16 bg-stone-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-serif text-stone-900 mb-8 text-center">
                Gredzenu ar dimantiem veidi
              </h2>
              
              <div className="prose prose-lg max-w-none text-stone-700">
                <h3 className="text-2xl font-serif text-stone-900 mt-8 mb-4">Solitaire gredzeni</h3>
                <p>
                  Klasiskais <strong>solitaire</strong> gredzens ir vispopulārākā izvēle saderināšanās gredzeniem. 
                  Viens skaists dimants centrā — vienkāršs un elegants dizains, kas nekad neiziet no modes.
                </p>

                <h3 className="text-2xl font-serif text-stone-900 mt-8 mb-4">Halo gredzeni</h3>
                <p>
                  <strong>Halo</strong> gredzeni apņem centrālo dimantu ar mazāku dimantu loku. 
                  Šis stils vizuāli palielina gredzenu un piešķir tam papildu mirdzumu.
                </p>

                <h3 className="text-2xl font-serif text-stone-900 mt-8 mb-4">Bezel gredzeni</h3>
                <p>
                  <strong>Bezel</strong> ietvars pilnībā apņem dimanta malu ar metālu. 
                  Tas ir drošākais stiprinājuma veids un ideāli piemērots aktīvam dzīvesveidam.
                </p>

                <h3 className="text-2xl font-serif text-stone-900 mt-8 mb-4">Cenas un kvalitāte</h3>
                <p>
                  Pure Atelier piedāvā gredzenu ar dimantiem plašā cenu diapazonā — no €1,150 līdz €3,500 un vairāk. 
                  Cena atkarīga no dimanta izmēra, kvalitātes un izvēlētā metāla.
                </p>
              </div>

              <div className="mt-12 text-center">
                <Link to="/gallery?category=rings">
                  <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-8">
                    Apskatīt visus gredzenus <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-stone-900 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-serif mb-4">Izvēlieties savu gredzenu ar dimantu</h2>
            <p className="text-stone-300 mb-8 max-w-2xl mx-auto">
              Sazināties ar mums, lai saņemtu personalizētu piedāvājumu un bezmaksas konsultāciju.
            </p>
            <a href="https://wa.me/37128448991?text=Sveiki!%20Interesē%20gredzeni%20ar%20dimantiem." target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-green-600 hover:bg-green-700 text-white px-8">
                <MessageCircle className="mr-2 h-5 w-5" /> WhatsApp konsultācija
              </Button>
            </a>
          </div>
        </section>
      </div>
    </>
  );
};

export default GredzeniArDimantiem;
